import { ValidatorFn } from '@angular/forms';
import { PurchaseProduct } from '../../core/api/products/entities/product-action.entity';
import { Product } from '../../core/api/products/entities/product.entity';
import { QuantityUnit } from '../../core/api/quantity-units/entities/quantity-unit.entity';
import { EntityReference } from '../../core/entities/shared/entity-reference.entity';
import { ProductWithBarcode } from '../../core/models/product-with-barcode.model';
import { requiredNotNull } from '../form-control-options.constants';
import { ShoppingItemFormControls } from '../form-controls/shopping-item.form-controls';
import { ShoppingItemFormOptions } from '../form-options/shopping-item.form-options';
import { BaseForm } from './base.form';

export class ShoppingItemForm extends BaseForm<ShoppingItemFormControls> {
  formGroup = this.createFormGroup(ShoppingItemFormControls);
  override options = new ShoppingItemFormOptions();
  _product?: ProductWithBarcode;

  get actualPrice(): number {
    return this.fullPrice - this.totalDiscount;
  }

  get amount(): number {
    return this.controls.amount.value || 1;
  }

  get conversionFactor(): number {
    return this.product?.conversionPurchaseToStockFactor ?? 1;
  }

  get enteredPurchaseQuPrice(): number {
    return this.controls.purchaseQuPrice.value || 0;
  }

  get fullPrice(): number {
    return this.amount * this.purchaseQuPrice;
  }

  get newStock(): string {
    const currentStock = this.product?.barcode?.amount ?? 0;
    const newAmount = currentStock + this.amount * this.conversionFactor;

    const quantityUnit =
      newAmount === 1 ? this.product?.quantityUnitStock.name : this.product?.quantityUnitStock.namePlural;

    return `${currentStock} → ${newAmount} ${quantityUnit}`;
  }

  get pantIncluded(): boolean {
    return this.controls.pantIncluded.value || false;
  }

  get product(): ProductWithBarcode | undefined {
    return this._product;
  }

  set product(product: ProductWithBarcode | undefined) {
    this._product = product;

    if (product) {
      this.controls.amount.setValue(product.barcode?.amount || 1);
      this.setInitialBestBeforeDate(product);
      this.setInitialLocation(product);
      this.setInitialPrice(product);
    }

    this.setChangeListener();
  }

  get purchaseQuPant(): number {
    return this.controls.purchaseQuPant.value || 0;
  }

  get purchaseQuPrice(): number {
    if (this.pantIncluded) {
      return this.enteredPurchaseQuPrice - this.purchaseQuPant;
    } else return this.enteredPurchaseQuPrice;
  }

  get quantityUnit(): QuantityUnit | undefined {
    return this.product?.quantityUnitPurchase;
  }

  get quantityUnitName(): string {
    if (this.quantityUnit) {
      return this.amount === 1 ? this.quantityUnit.name : this.quantityUnit.namePlural;
    } else return '';
  }

  get totalDiscount(): number {
    return this.controls.totalDiscount.value || 0;
  }

  get totalPant(): number {
    return this.amount * this.purchaseQuPant;
  }

  disable(): void {
    this.controls.amount.disable();
    this.controls.bestBeforeDate.disable();
    this.controls.neverOverdue.disable();
    this.controls.pantIncluded.disable();
    this.controls.purchaseQuPant.disable();
    this.controls.purchaseQuPrice.disable();
    this.controls.totalDiscount.disable();
  }

  enable(): void {
    this.controls.amount.enable();
    this.controls.neverOverdue.enable();
    this.controls.pantIncluded.enable();
    this.controls.purchaseQuPant.enable();
    this.controls.purchaseQuPrice.enable();
    this.controls.totalDiscount.enable();

    if (this.controls.neverOverdue.value === false) {
      this.controls.bestBeforeDate.enable();
    }
  }

  getProductAction(purchased: Date, store: EntityReference): PurchaseProduct {
    const amount = this.amount * this.conversionFactor;
    const price = this.actualPrice / amount;

    return {
      amount,
      expiresAt: this.controls.neverOverdue.value
        ? '2999-12-31'
        : this.controls.bestBeforeDate.value!.toISOString().split('T')[0],
      locationId: this.controls.location.value!.id,
      price,
      productId: this.product!.id,
      purchasedAt: purchased.toISOString().split('T')[0],
      storeId: store.id,
    };
  }

  private setChangeListener(): void {
    this.controls.neverOverdue.valueChanges.subscribe(value => {
      if (this.controls.neverOverdue.disabled) {
        return;
      }

      if (value === false) {
        this.controls.bestBeforeDate.setValidators(requiredNotNull.validators as ValidatorFn[]);
        this.controls.bestBeforeDate.setValue(new Date());
        this.controls.bestBeforeDate.enable();
      } else {
        this.controls.bestBeforeDate.clearValidators();
        this.controls.bestBeforeDate.setValue(null);
        this.controls.bestBeforeDate.disable();
      }
    });
  }

  private setInitialBestBeforeDate({ nextDueAt }: Product): void {
    if (nextDueAt) {
      const nextDueDate = new Date(nextDueAt);

      if (nextDueDate < new Date('2999-12-31')) {
        this.controls.bestBeforeDate.setValue(nextDueDate);
        this.controls.neverOverdue.setValue(false);
        this.controls.bestBeforeDate.enable();
      } else {
        this.controls.neverOverdue.setValue(true);
        this.controls.bestBeforeDate.setValidators(requiredNotNull.validators as ValidatorFn[]);
        this.controls.bestBeforeDate.setValue(null);
        this.controls.bestBeforeDate.disable();
      }
    } else {
      this.controls.neverOverdue.setValue(true);
      this.controls.bestBeforeDate.clearValidators();
      this.controls.bestBeforeDate.setValue(null);
      this.controls.bestBeforeDate.disable();
    }
  }

  private setInitialLocation({ locationId }: Product): void {
    if (locationId) {
      this.controls.location.setValue(this.options.locations.find(l => l.id === locationId)!);
    }
  }

  private setInitialPrice({ prices }: Product): void {
    if (prices) {
      this.controls.purchaseQuPrice.setValue(prices.current * this.conversionFactor);
    }
  }
}
